import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { toast } from 'sonner';

export const EXPECTED_API_CONTRACT = 2;
export const API_CONTRACT_HEADER = 'x-vadio-api-contract';

const RELOADED_AT_KEY = 'vadio.api-contract.reloaded-at';
const RELOAD_GUARD_MS = 60_000;
const RELOAD_DELAY_MS = 1_500;

type ContractHandlerDeps = {
	expected: number;
	storage: Pick<Storage, 'getItem' | 'setItem'>;
	now: () => number;
	reload: () => void;
	notify: () => void;
	delayMs: number;
};

export type ContractHeaderInput = { response: Response };

const readContract = (headers: Headers): number | null => {
	const raw = headers.get(API_CONTRACT_HEADER);
	if (!raw) return null;
	const parsed = Number(raw);
	return Number.isInteger(parsed) ? parsed : null;
};

type ReloadStampInput = { storage: ContractHandlerDeps['storage']; now: number };

const reloadedRecently = ({ storage, now }: ReloadStampInput): boolean => {
	try {
		const stored = Number(storage.getItem(RELOADED_AT_KEY));
		return Number.isFinite(stored) && stored > 0 && now - stored < RELOAD_GUARD_MS;
	} catch {
		return false;
	}
};

const rememberReload = ({ storage, now }: ReloadStampInput): void => {
	try {
		storage.setItem(RELOADED_AT_KEY, String(now));
	} catch {
		return;
	}
};

export const createContractHandler = (deps: ContractHandlerDeps) => {
	let reloadScheduled = false;

	return ({ response }: ContractHeaderInput): void => {
		const served = readContract(response.headers);
		if (served === null || served === deps.expected) return;
		if (reloadScheduled) return;
		const now = deps.now();
		if (reloadedRecently({ storage: deps.storage, now })) return;

		reloadScheduled = true;
		rememberReload({ storage: deps.storage, now });
		deps.notify();
		setTimeout(deps.reload, deps.delayMs);
	};
};

export const handleContractHeader = createContractHandler({
	expected: EXPECTED_API_CONTRACT,
	storage: sessionStorage,
	now: () => Date.now(),
	reload: () => window.location.reload(),
	notify: () => toast.info(i18n._(msg`Vadio was updated, reloading`), { id: 'api-contract' }),
	delayMs: RELOAD_DELAY_MS,
});
